<template>
    <CForm :ref="componentName">
        <CContainer fluid>
            <CRow>
                <CCol>
                    <p class="mt-3">
                        Allows users to choose and reorder fields for customized
                        views, enhancing data presentation and usability.
                    </p>
                </CCol>
            </CRow>
            <CRow>
                <CCol>
                    <p class="mt-3">
                        Select the variable fields that will be displayed in the
                        result.
                    </p>
                </CCol>
            </CRow>
            <CRow>
                <CCol>
                    <CCTableAddItems
                        :items.sync="query.variableFields"
                        @addItem="addVariableFieldToQueryFields"
                        @deforeRemoveItem="removeVariableFieldFromQueryFields"
                        :fields="[
                            { key: 'key', label: 'field' },
                            { key: 'label', label: 'alias' },
                            {
                                key: '_idRef',
                                label: '_idRef',
                                _active: false,
                                _defaultValue: generateShortUniqueId,
                            },
                        ]"
                    >
                        <template #key="{ index }">
                            <SelectAsync
                                :options="fields"
                                :value.sync="query.variableFields[index].key"
                                :placeholder="$t('Select a field')"
                                optionsValue="key"
                                optionsLabel="label"
                            >
                            </SelectAsync>
                        </template>
                        <template #label="{ index }">
                            <CInput
                                :value.sync="query.variableFields[index].label"
                                :placeholder="$t('Enter an alias')"
                                class="mb-0"
                                size="sm"
                            />
                        </template>
                    </CCTableAddItems>
                </CCol>
            </CRow>
            <CRow>
                <CCol>
                    <p class="mt-3">
                        Select the fixed fields that will be displayed in the
                        result.
                    </p>
                </CCol>
            </CRow>
            <CRow>
                <CCol>
                    <CCTableAddItems
                        :items.sync="query.fixedFields"
                        @addItem="addVariableFieldToQueryFields"
                        @deforeRemoveItem="removeFixedFieldFromQueryFields"
                        :fields="[
                            { key: 'key', label: 'field' },
                            { key: 'value', label: 'value' },
                            {
                                key: '_idRef',
                                label: '_idRef',
                                _active: false,
                                _defaultValue: generateShortUniqueId,
                            },
                            {
                                key: 'type',
                                label: 'type',
                                _active: false,
                                _defaultValue: 'FixedField',
                            },
                        ]"
                    >
                        <template #key="{ index }">
                            <CInput
                                :value.sync="query.fixedFields[index].key"
                                :placeholder="$t('Enter an name')"
                                class="mb-0"
                                size="sm"
                            />
                        </template>
                        <template #value="{ index }">
                            <CInput
                                :value.sync="query.fixedFields[index].value"
                                :placeholder="$t('Enter an alias')"
                                class="mb-0"
                                size="sm"
                            />
                        </template>
                    </CCTableAddItems>
                </CCol>
            </CRow>

            <CRow>
                <CCol>
                    <p class="mt-3">Sort the fields by drag and drop.</p>
                </CCol>
            </CRow>
            <CRow>
                <CCol class="p-0"></CCol>
                <CCol class="p-0" md="8" sm="8" lg="4">
                    <draggable
                        class="list-group"
                        tag="ul"
                        v-model="query.fields"
                        v-bind="dragOptions"
                        :move="onMove"
                        @end="orderVariableField"
                    >
                        <transition-group type="transition" :name="'flip-list'">
                            <li
                                class="list-group-item"
                                v-for="(element, index) in query.fields"
                                :key="element + index"
                            >
                                <div
                                    class="d-flex justify-content-between align-items-top"
                                >
                                    <span>
                                        {{
                                            element.label
                                                ? element.label
                                                : element.key
                                        }}
                                    </span>
                                    <CButton
                                        @click="disableItem(element)"
                                        class="button__plain"
                                        v-if="!element.fixed"
                                    >
                                        <CIcon :content="$options.icons.cilX" />
                                    </CButton>
                                </div>
                            </li>
                        </transition-group>
                    </draggable>
                </CCol>
                <CCol class="p-0"></CCol>
            </CRow>
            <CRow>
                <CCol>
                    <p>
                        Grouping them based on fields like email, phone
                        efficiently.
                    </p>
                </CCol>
            </CRow>
            <CRow>
                <CCol>
                    <SelectTags
                        :placeholder="$t('Group by')"
                        size="sm"
                        :value.sync="query.groupBy"
                        :onlyValue="true"
                        :options="[
                            {
                                name: 'Email',
                                value: 'Email',
                            },
                            {
                                name: 'FullPhone',
                                value: 'FullPhone',
                            },
                        ]"
                        :label="$t('Group by')"
                    />
                </CCol>
            </CRow>
            <CRow>
                <CCol>
                    <p>
                        Specify criteria to refine query results, including
                        filters by values, ranges, or conditions. Define limits
                        on the number of records and apply advanced segmentation
                        for precise data extraction.
                    </p>
                </CCol>
            </CRow>
            <CRow>
                <CCol>
                    <vue-query-builder
                        :rules="rules"
                        v-model="query"
                        :labels="defaultLabels"
                        :maxDepth="3"
                    >
                        <template v-slot:default="slotProps">
                            <CCQueryBuilderGroup
                                v-bind="slotProps"
                                :query.sync="query"
                            />
                        </template>
                    </vue-query-builder>
                </CCol>
            </CRow>
            <!--<CRow>
                <CCol>
                    <pre>{{ query }}</pre>
                    <pre>{{ getQueryFromVueQueryBuilder(query) }}</pre>
                </CCol>
            </CRow>-->
        </CContainer>
    </CForm>
</template>

<script>
/** Mixins */

import { validationMixin } from "vuelidate";
import baseMixin from "~/mixins/base";
import VueQueryBuilder from "vue-query-builder";
import CCQueryBuilderGroup from "./CCQueryBuilderGroup.vue";
import CCInputEditable from "~/components/CCInputEditable";
import draggable from "vuedraggable";
import { cilX, cilPencil, cilSave } from "@coreui/icons";
import SelectAsync from "£lement/SelectAsync";
import SelectTags from "£lement/SelectTags";
import TableFixedFields from "~/components/TableFixedFields.vue";
import CCTableAddItems from "~/components/CCTableAddItems.vue";
import { generateShortUniqueId } from "../../../../utils";
import CCButtonUploadFile from "~/components/CCButtonUploadFile.vue";
import CCUploadSimpleFilterFile from "~/components/CCUploadSimpleFilterFile"; 
import { SEGMENTATIONS_USERS_QUERY_MODEL_ID } from "../config/consts";


const defaultRuleType = "text";
const defaultOperators = [
    "equal",
    "like",
    {
        label: "Not like",
        value: "notLike",
    },
    {
        label: "empty",
        value: "empty",
        type: "radio",
        choices: [
            {
                label: "Yes",
                value: true,
            },
            {
                label: "No",
                value: false,
            },
        ],
    },
    {
        label: "Between",
        value: "between",
        type: "between",
    },
    "in",
    "notIn",
    {
        label: "In file",
        value: "inFile",
        type: "inFile",
    },
    {
        label: "Not in file",
        value: "notInFile",
        type: "notInFile",
    },
    {
        label: "In sub query",
        value: "inSubQuery",
        type: "inSubQuery",
    },
    {
        label: "Not in sub query",
        value: "notInSubQuery",
        type: "notInSubQuery",
    },
    {
        label: "Greater than",
        value: "greaterThan",
    },
    {
        label: "Less than",
        value: "lessThan",
    },
    "regExp",
    "startBy",
    "notStartBy",
];

const defaultRules = [
    {
        type: "text",
        id: "LeadId",
        label: "LeadId",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "ProviderName",
        label: "ProviderName",
        operators: ["contains", "equal", "like"],
    },
    {
        type: "text",
        id: "Email",
        label: "Email",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "FullPhone",
        label: "FullPhone",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "NationalPhone",
        label: "NationalPhone",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "FirstName",
        label: "FirstName",
        operators: ["contains", "equal", "like"],
    },
    {
        type: "text",
        id: "LastName",
        label: "LastName",
        operators: ["contains", "equal", "like"],
    },
    {
        type: "text",
        id: "BirthDate",
        label: "BirthDate",
        operators: defaultOperators,
    },
    {
        type: "text",
        id: "Age",
        label: "Age",
        operators: defaultOperators,
    },
    {
        type: "select",
        id: "Gender",
        label: "Gender",
        choices: [
            {
                label: "Man",
                value: "M",
            },
            {
                label: "Famale",
                value: "F",
            },
        ],
    },
    {
        type: "text",
        id: "LastPullphoningStatus",
        label: "LastPullphoningStatus",
        operators: ["equal", "like"],
    },
    {
        type: "select",
        id: "ProviderCountryId",
        label: "ProviderCountry",
        choices: [
            {
                label: "Spain",
                value: "999",
            },
        ],
    },
    {
        type: "text",
        id: "Cp",
        label: "Cp",
        operators: ["equal", "like", "in"],
    },
];
export default {
    name: "QueryBuilderForm",
    mixins: [validationMixin, baseMixin],
    components: {
        VueQueryBuilder,
        CCQueryBuilderGroup,
        TableFixedFields,
        CCInputEditable,
        draggable,
        SelectAsync,
        SelectTags,
        CCTableAddItems,
        CCButtonUploadFile,
        CCUploadSimpleFilterFile
    },
    icons: {
        cilX,
        cilPencil,
        cilSave,
    },
    data() {
        return {
            formDataComponent: {
                query: {},
            },

            defaultLabels: {
                matchType: "Match Type",
                matchTypes: [
                    { id: "all", label: "All" },
                    { id: "any", label: "Any" },
                ],
                limit: "Limit",
                addRule: "Add Rule",
                removeRule: "&times;",
                addGroup: "Add Group",
                removeGroup: "&times;",
                textInputPlaceholder: "value",
                textSelectRulePlaceholder: "Select a field",
            },

            fields: defaultRules.map((rule, index) => {
                return {
                    key: rule.id,
                    label: rule.label,
                    fixed: false,
                    _active: false,
                    _editionActived: false,
                };
            }),

            // selectedField: null,

            editable: true,
            rules: defaultRules,

            defaultQuery: {
                logicalOperator: "all",
                children: [],
                fields: [],
                groupBy: [],
                limit: null,
            },

            query: {
                logicalOperator: "all",
                children: [],
                fields: [], //fields
                variableFields: [],
                fixedFields: [],
                groupBy: [],
                limit: null,
            },

            _loading: false,
        };
    },
    props: {
        formData: {
            type: Object,
        },
        loading: Boolean,
    },
    computed: {
        componentName() {
            return this.$options.name;
        },
        // disabledFields() {
        //     return this.fields.filter((field) => {
        //         return !field._active;
        //     });
        // },
        dragOptions() {
            return {
                animation: 0,
                group: "description",
                disabled: !this.editable,
                ghostClass: "ghost",
            };
        },
    },
    watch: {
        formData() {
            this.formDataComponent.query = this.formData.query;
            this.query = this.getVueQueryBuilderFromQuery(
                this.formDataComponent.query
            );
        },
        // selectedField(newValue, oldValue) {
        //     if (newValue) {
        //         const findIndex = this.fields.findIndex((field) => {
        //             return field.key == newValue;
        //         });
        //         if (findIndex != -1) {
        //             this.fields[findIndex]._active = true;
        //         }
        //         this.query.fields.push(this.fields[findIndex]);
        //         this.selectedField = null;
        //     }
        // },
    },
    methods: {
        generateShortUniqueId,
        getFormData() {
            return {
                query: {
                    ...this.formDataComponent.query,
                    ...this.getQueryFromVueQueryBuilder(this.query),
                },
            };
        },
        onMove({ relatedContext, draggedContext }) {
            const relatedElement = relatedContext.element;
            const draggedElement = draggedContext.element;
            return (
                (!relatedElement || !relatedElement.fixed) &&
                !draggedElement.fixed
            );
        },
        addVariableFieldToQueryFields(item) {
            try {
                this.query.fields.push(item);
            } catch (error) {
                console.log("Field not found");
            }
        },

        removeFixedFieldFromQueryFields({ position }) {
            const fixedFieldRemoved = this.query.fixedFields[position];
            const findIndex = this.query.fields.findIndex((field) => {
                return field._idRef == fixedFieldRemoved._idRef;
            });

            if (findIndex != -1) {
                this.query.fields.splice(findIndex, 1);
            }
        },
        removeVariableFieldFromQueryFields({ position }) {
            const variableFieldRemoved = this.query.variableFields[position];

            const findIndex = this.query.fields.findIndex((field) => {
                return field._idRef == variableFieldRemoved._idRef;
            });

            if (findIndex != -1) {
                this.query.fields.splice(findIndex, 1);
            }
        },

        orderVariableField(event) {
            let newOrderVariableFields = [];
            let newOrderFixedFields = [];
            for (const field of this.query.fields) {
                // Si el campo no se encuentra verificamos que no sea un campo fijo
                if (field.type && field.type == "FixedField") {
                    newOrderFixedFields.push(field);
                } else {
                    newOrderVariableFields.push(field);
                }
            }

            this.query.variableFields = newOrderVariableFields;
            this.query.fixedFields = newOrderFixedFields;
        },
        disableItem(item) {
            const indexInActiveFields = this.query.fields.findIndex((field) => {
                return field.key == item.key;
            });
            const indexInFields = this.fields.findIndex((field) => {
                return field.key == item.key;
            });
            if (indexInActiveFields !== -1) {
                this.query.fields.splice(indexInActiveFields, 1);
            }
            if (indexInFields !== -1) {
                this.fields[indexInFields]._active = false;
            }
        },
        getQueryFromVueQueryBuilder(query) {
            const vueQueryBuilder = {
                type: query.type,
                logicalOperator: query.logicalOperator,
                children: query.children,
                fields: query.fields || [],
                limit: query.limit == "" ? undefined : query.limit,
                groupBy: query.groupBy || [],
            };
            const queryBuilderGroupType = "query-builder-group";
            const queryBuilderRuleType = "query-builder-rule";

            const queryBuilderTypes = {
                [queryBuilderGroupType]: "GroupCondition",
                [queryBuilderRuleType]: "Condition",
            };

            const logicalOperators = {
                all: "AND",
                any: "OR",
            };

            let whereQueryBuilder = [];
            for (const child of vueQueryBuilder.children) {
                if (child.type !== queryBuilderGroupType) {
                    whereQueryBuilder.push({
                        type: queryBuilderTypes[child.type],
                        field: child.query.rule,
                        condition: child.query.operator,
                        value: child.query.value,
                        logicalOperator:
                            logicalOperators[vueQueryBuilder.logicalOperator],
                    });
                } else if (child.type === queryBuilderGroupType) {
                    whereQueryBuilder.push({
                        ...this.getQueryFromVueQueryBuilder({
                            type: queryBuilderTypes[child.type],
                            ...child.query,
                        }),
                        logicalOperator:
                            logicalOperators[vueQueryBuilder.logicalOperator],
                    });
                } else {
                    throw new Error("Invalid query builder type");
                }
            }

            const fieldsQueryBuilder = vueQueryBuilder.fields.map((field) => {
                if (!field.key) {
                    throw new Error("Field invalid");
                }
                let mappingField = {
                    field: field.key,
                    alias: field.label,
                };
                if (field.type && field.type == "FixedField") {
                    mappingField = {
                        ...mappingField,
                        type: field.type,
                        value: field.value,
                    };
                }
                return mappingField;
            });

            return {
                type: vueQueryBuilder.type,
                where: whereQueryBuilder,
                fields: fieldsQueryBuilder,
                limit: vueQueryBuilder.limit,
                groupBy: vueQueryBuilder.groupBy,
            };
        },

        getVueQueryBuilderFromQuery(query) {
            if (!query) return this.defaultQuery;

            const types = {
                GroupCondition: "query-builder-group",
                Condition: "query-builder-rule",
            };

            const logicalOperators = {
                AND: "all",
                OR: "any",
            };

            const whereQueryBuilder = query.where ? query.where : [];
            const fieldsQueryBuilder = query.fields ? query.fields : [];
            const groupByQueryBuilder = query.groupBy ? query.groupBy : [];
            const firstLogicalOperador =
                whereQueryBuilder.length > 0
                    ? whereQueryBuilder[0].logicalOperator
                    : "AND"; // Tomamos por defecto la primera condición como el operador lógico.
            const vueQueryBuilder = {
                logicalOperator: logicalOperators[firstLogicalOperador],
                children: [],
                fields: [],
                variableFields: [],
                fixedFields: [],
                groupBy: groupByQueryBuilder,
                limit: query.limit ? query.limit : null,
            };

            for (const where of whereQueryBuilder) {
                if (where.type === "Condition") {
                    vueQueryBuilder.children.push({
                        type: types[where.type],
                        query: {
                            rule: where.field,
                            operand: where.field,
                            value: where.value,
                            operator: where.condition,
                            logicalOperator: where.logicalOperator,
                        },
                    });
                } else if (where.type === "GroupCondition") {
                    vueQueryBuilder.children.push({
                        type: types[where.type],
                        // query: {
                        //     logicalOperator: where.logicalOperator,
                        //     children: [],
                        //     limit: where.limit,
                        // },
                        query: this.getVueQueryBuilderFromQuery(where),
                    });
                }
            }
            // for (const [key, value] of Object.entries(whereQueryBuilder)) {
            //     const [condition, conditionValue] = Object.entries(value)[0];
            //     vueQueryBuilder.children.push({
            //         type: "query-builder-rule",
            //         query: {
            //             rule: key,
            //             operand: key,
            //             value: conditionValue,
            //             operator: condition,
            //         },
            //     });
            // }

            for (const field of fieldsQueryBuilder) {
                try {
                    const findField =
                        this.getVueQueryFieldFromQueryField(field);
                    vueQueryBuilder.fields.push(findField);
                    vueQueryBuilder.variableFields.push(findField);
                } catch (error) {
                    // Si el campo no se encuentra verificamos que no sea un campo fijo
                    if (field.type && field.type == "FixedField") {
                        const _idRef = generateShortUniqueId();
                        vueQueryBuilder.fields.push({
                            type: field.type,
                            key: field.field,
                            label: field.alias ? field.alias : field.field,
                            value: field.value,
                            _idRef,
                        });
                        vueQueryBuilder.fixedFields.push({
                            key: field.field,
                            label: field.alias ? field.alias : field.field,
                            value: field.value,
                            _idRef,
                        });
                    }
                }
            }
            return vueQueryBuilder;
        },

        getVueQueryFieldFromQueryField(field) {
            let vueQueryField = {};
            const findFieldIndex = this.fields.findIndex((item) => {
                if (field.field) {
                    return item.key == field.field;
                } else {
                    return item.key == field;
                }
            });

            if (findFieldIndex != -1) {
                this.fields[findFieldIndex]._active = true;
                vueQueryField = {
                    ...this.fields[findFieldIndex],
                    label: field.field
                        ? field.alias
                        : this.fields[findFieldIndex].label,
                    _idRef: generateShortUniqueId(),
                };
                return vueQueryField;
            } else {
                throw new Error("Field not found");
            }
        },

        getRulesFromQueryModel(queryModel) {
            let rules = [];
            const queryModelFields = queryModel.fields || [];
            for (const field of queryModelFields) {
                rules.push({
                    type: defaultRuleType,
                    id: field,
                    label: field,
                    operators: defaultOperators,
                });
            }
            return rules;
        },

        getFieldsFromRules(rules) {
            return rules.map((rule, index) => {
                return {
                    key: rule.id,
                    label: rule.label,
                    fixed: false,
                    _active: false,
                    _editionActived: false,
                };
            });
        },

        async loadQueryModel() {
            const { data } = await this.fetcher({
                baseURL: process.env.VUE_APP_API_SASS,
                url: `/segmentations/query-models/${SEGMENTATIONS_USERS_QUERY_MODEL_ID}`,
                method: "GET",
            });
            const queryModel = {
                fields: data.fields,
            };
            this.rules = this.getRulesFromQueryModel(queryModel);
            this.fields = this.getFieldsFromRules(this.rules);
        },
    },

    async mounted() {
        this.formDataComponent.query = this.formData.query;
        this.query = this.getVueQueryBuilderFromQuery(
            this.formDataComponent.query
        );
        await this.loadQueryModel();
    },
};
</script>

<style></style>
